import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

const VIEW = 280;   // crop viewport (square) in CSS px
const OUTPUT = 400; // output square in px

/**
 * Profile photo cropper.
 * - Pan: drag the image inside the circular mask
 * - Zoom: slider from 1x to 3x
 * - Apply: produces a 400x400 JPEG data URL and calls onApply.
 *
 * Pointer events are used so the same code handles touch + mouse.
 */
export default function ProfilePhotoCropper({ open, imageDataUrl, onApply, onCancel }) {
  const [img, setImg] = useState(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, ox: 0, oy: 0 });

  // Load the image whenever a new data URL is provided
  useEffect(() => {
    if (!imageDataUrl) { setImg(null); return; }
    const i = new Image();
    i.onload = () => {
      setImg(i);
      // Reset transform on a new image, scaled to fully cover the viewport.
      setScale(1);
      setOffset({ x: 0, y: 0 });
    };
    i.src = imageDataUrl;
  }, [imageDataUrl]);

  // Base size: scale so the image's shorter side fits the viewport (cover).
  const base = img ? VIEW / Math.min(img.width, img.height) : 1;
  const drawW = img ? img.width * base * scale : 0;
  const drawH = img ? img.height * base * scale : 0;

  // Clamp offset so the image always covers the viewport (no gaps at edges).
  const clamp = (ox, oy) => {
    const maxX = Math.max(0, (drawW - VIEW) / 2);
    const maxY = Math.max(0, (drawH - VIEW) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, ox)),
      y: Math.min(maxY, Math.max(-maxY, oy)),
    };
  };

  useEffect(() => {
    // Re-clamp when the scale changes so we don't have gaps.
    setOffset((o) => clamp(o.x, o.y));
  }, [scale, img]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(clamp(dragRef.current.ox + dx, dragRef.current.oy + dy));
  };
  const onPointerUp = () => { dragRef.current.active = false; };

  const apply = () => {
    if (!img) return;
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext("2d");
    // The viewport shows the image at: position (centre + offset), size (drawW x drawH).
    // We need to map that to a 400x400 output canvas.
    const ratio = OUTPUT / VIEW;
    const w = drawW * ratio;
    const h = drawH * ratio;
    const cx = OUTPUT / 2 + offset.x * ratio;
    const cy = OUTPUT / 2 + offset.y * ratio;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, OUTPUT, OUTPUT);
    ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
    onApply(canvas.toDataURL("image/jpeg", 0.85));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="bg-[#FFFFFF] border-[#E5E9F0] max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Adjust your photo</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[#7A828C] -mt-2">Drag to reposition, use the slider to zoom.</p>

        {/* Crop viewport */}
        <div
          className="relative mx-auto rounded-sm bg-[#E6F8FC] overflow-hidden select-none touch-none"
          style={{ width: VIEW, height: VIEW, cursor: "grab" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="profile-photo-cropper-viewport"
        >
          {img && (
            <img
              src={imageDataUrl}
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                width: drawW,
                height: drawH,
                maxWidth: "none",
                maxHeight: "none",
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
          )}
          {/* Circular mask overlay */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              boxShadow: `0 0 0 ${VIEW}px rgba(255,255,255,0.85)`,
              borderRadius: "50%",
              margin: 0,
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 rounded-full border-2 border-[#000000] pointer-events-none"
          />
        </div>

        <div className="space-y-2">
          <label className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">Zoom</label>
          <Slider
            value={[scale]}
            min={1}
            max={3}
            step={0.05}
            onValueChange={(v) => setScale(v[0])}
            data-testid="profile-photo-cropper-zoom"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} className="flex-1 h-11 rounded-sm" data-testid="profile-photo-cropper-cancel">
            Cancel
          </Button>
          <Button onClick={apply} className="flex-1 h-11 rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="profile-photo-cropper-apply">
            Use this photo
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
